import { LocalStorageKeys } from '@/enums';
import { useDepartmentStore } from '@/stores/department';
import { useInstitutionStore } from '@/stores/institution';
import { ref } from 'vue';

type MatomoStatus = 'none' | 'cancelled' | 'ready';

const status = ref<MatomoStatus>('none');

const DIMENSION_INSTITUTION = 1;
const DIMENSION_DEPARTMENT = 2;

export const init = (): void => {
  if (status.value !== 'none') {
    return;
  }

  if (import.meta.env.DEV) {
    status.value = 'cancelled';
    return;
  }

  status.value = 'ready';

  const _paq = (window._paq = window._paq || []);
  (function () {
    const u = 'https://aws.proreact.dk/tracking/';
    _paq.push(['enableLinkTracking']);
    _paq.push(['setTrackerUrl', u + 'matomo.php']);
    _paq.push(['setSiteId', '10']);

    const d = document;
    const g = d.createElement('script');
    const s = d.getElementsByTagName('script')[0];

    g.type = 'text/javascript';
    g.async = true;
    g.defer = true;
    g.src = u + 'matomo.js';

    if (s && s.parentNode) {
      s.parentNode.insertBefore(g, s);
    }
  })();
};

export type EventAction = 'Click' | 'View' | 'Select';

export type EventCategory =
  | 'ScreenDimmer'
  | 'EnterPin'
  | 'ViewType'
  | 'NewBooking'
  | 'BookingEntry'
  | 'Navigation';

export function useMatomo() {
  init();
  const departmentStore = useDepartmentStore();
  const institutionStore = useInstitutionStore();

  const _paq = (window._paq = window._paq || []);

  const setDimension = () => {
    _paq.push([
      'setCustomDimension',
      DIMENSION_INSTITUTION,
      institutionStore.id,
    ]);
    _paq.push(['setCustomDimension', DIMENSION_DEPARTMENT, departmentStore.id]);
  };

  const trackEvent = (
    action: EventAction,
    category: EventCategory,
    extraInfo?: string
  ) => {
    if (localStorage.getItem(LocalStorageKeys.ActivateMatomoLogs)) {
      console.log(
        `📊 Category: ${category}\n🎯 Action: ${action}\n📝 Extra Info: ${extraInfo || '(none)'}`
      );
    }

    if (status.value !== 'ready') {
      return;
    }

    setDimension();
    _paq.push(['trackEvent', category, action, extraInfo || '']);
  };

  const trackPageView = (title: string) => {
    if (localStorage.getItem(LocalStorageKeys.ActivateMatomoLogs)) {
      console.log(`📊 Page View: ${title}`);
    }

    if (status.value !== 'ready') {
      return;
    }

    setDimension();

    const cleanUrl = new URL(window.location.href);
    cleanUrl.search = '';

    _paq.push(['setDocumentTitle', title]);
    _paq.push(['setCustomUrl', cleanUrl.toString()]);
    _paq.push(['trackPageView']);
  };

  return {
    trackEvent,
    trackPageView,
  };
}
